import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccountConsent } from "../../hooks/useAccountConsent";
import { isGateTopic } from "../../lib/consentSession";
import { openConsentPage } from "./ConsentChecklist";

/** localStorage: version ids whose "updated" banner the user closed (one per version). */
export const UPDATED_DISMISSED_KEY = "consent-updated-dismissed";

function readDismissed(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(UPDATED_DISMISSED_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function formatDate(iso: string, language: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/**
 * Non-material change of the terms or the privacy notice (Figma "Web / Privacy / Updated notice / Banner on wall"
 * 4426:2693, Dark 4428:3447): "We updated our privacy notice on {{date}}. Read it", closable; closed once per
 * version id. Nothing to accept — a material change goes through the re-consent dialog instead.
 */
export default function UpdatedNoticeBanner() {
  const { t, i18n } = useTranslation();
  const { data } = useAccountConsent();
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);

  const topic = data?.topics.find((x) => isGateTopic(x) && x.updated && !dismissed.includes(x.versionId));
  if (!topic) return null;

  function dismiss() {
    if (!topic) return;
    const next = [...readDismissed().filter((x) => x !== topic.versionId), topic.versionId];
    localStorage.setItem(UPDATED_DISMISSED_KEY, JSON.stringify(next));
    setDismissed(next);
  }

  const date = formatDate(topic.effectiveAt, i18n.language);
  const message =
    topic.key === "terms"
      ? t("consent_updated_terms", { date })
      : t("consent_updated_privacy_notice", { date });

  return (
    <div
      role="status"
      data-testid="consent-updated-banner"
      className="mx-auto mt-3 flex w-[calc(100%-2rem)] max-w-[720px] items-center gap-3 rounded-xl bg-[var(--primary-soft)] px-4 py-2.5 text-sm text-[var(--foreground)]"
    >
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-[var(--primary-text)]">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
      <span className="min-w-0 flex-1">
        {message}{" "}
        {topic.url && (
          <button
            type="button"
            data-testid="consent-updated-banner-read"
            onClick={() => openConsentPage(topic)}
            className="border-none bg-transparent p-0 text-sm font-semibold text-[var(--primary-text)] cursor-pointer"
          >
            {t("consent_read_it")}
          </button>
        )}
      </span>
      <button
        type="button"
        data-testid="consent-updated-banner-close"
        aria-label={t("consent_close")}
        onClick={dismiss}
        className="shrink-0 border-none bg-transparent p-1 text-[var(--foreground-muted)] cursor-pointer"
      >
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
